// Fees → Policies → detail. Full-page view of one fee policy: rates
// on the left, consequences on the right (how it resolves, which
// markets it touches, its history). Replaces the old 420px inspector
// side panel.
//
// Saving never mutates in place: the service versions policies, so a
// save creates the next version and this page navigates to it. The
// audit trail keeps the full lineage.
//
// Currency (SM-503) rides in the right-hand column: "What this prices"
// names the books this policy's markets run and quotes the rate in each
// book's own currency, from the FORM's values, so the preview answers
// "what am I about to change, and for whom" before the save.
import {
  createFileRoute,
  Link,
  useNavigate,
} from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  archiveFeeProfile,
  cancelScheduledFeeProfile,
  createFeeProfile,
  fetchCategories,
  fetchFeeAudit,
  fetchFeeProfile,
  fetchFeeProfiles,
  fetchRule,
  fetchRules,
  feeKeys,
  keys,
  revenueKeys,
  type FeeProfile,
} from "@/lib/queries";
import { APIError } from "@/lib/api";
import { LoadFailed } from "@/components/fees/LoadFailed";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { ConfirmModal } from "@/components/ui/ConfirmModal";
import { ErrorPanel } from "@/components/ui/ErrorPanel";
import { Skeleton } from "@/components/ui/Skeleton";
import { FeeRateInput } from "@/components/fees/FeeRateInput";
import { ScheduleField } from "@/components/fees/ScheduleField";
import { BrokerCeilingHint } from "@/components/fees/BrokerCeilingHint";
import { AuditList } from "@/components/fees/AuditList";
import {
  fmtPct,
  redemptionRateOf,
  scopeNoun,
  scopeTone,
  setsRedemptionRate,
} from "@/components/fees/policy";
import {
  percentLabel,
  RedemptionFeeField,
  type RedemptionMode,
} from "@/components/fees/RedemptionFeeField";
import { useMyPerms } from "@/hooks/useMyPerms";
import { Perm } from "@/lib/perms";
import { useFeeReach } from "@/components/fees/useFeeReach";
import { FeeReachPanel } from "@/components/fees/FeeReachPanel";
import {
  FeeCurrencyMarks,
  PolicyCurrencyCell,
} from "@/components/fees/FeeCurrencyLabel";
import {
  exampleCurrency,
  isLegacyPolicy,
  LEGACY_POLICY_NOTE,
  redemptionChargeNote,
} from "@/components/fees/feeCurrency";

export const Route = createFileRoute(
  "/_app/treasury/fees/policies/$profileId",
)({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData({
      queryKey: feeKeys.profile(params.profileId),
      queryFn: () => fetchFeeProfile(params.profileId),
    }),
  component: PolicyDetailPage,
});

const PAGE_FETCH_LIMIT = 200;

function PolicyDetailPage() {
  const { profileId } = Route.useParams();
  const profileQ = useQuery({
    queryKey: feeKeys.profile(profileId),
    queryFn: () => fetchFeeProfile(profileId),
  });

  if (profileQ.isError) {
    return <ErrorPanel error={profileQ.error} reset={() => profileQ.refetch()} />;
  }
  if (!profileQ.data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  return <PolicyDetail profile={profileQ.data} />;
}

function PolicyDetail({ profile }: { profile: FeeProfile }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const precision = profile.fee_rate_precision ?? 6;
  const isGlobal = profile.scope === "GLOBAL";
  const isScheduled = profile.status === "SCHEDULED";
  const isArchived = profile.status === "ARCHIVED";

  // Lookup data shares cache keys with the Policies list page.
  const globalQ = useQuery({
    queryKey: feeKeys.profiles("GLOBAL"),
    queryFn: () => fetchFeeProfiles("GLOBAL"),
    staleTime: 30_000,
  });
  const categoryProfilesQ = useQuery({
    queryKey: feeKeys.profiles("CATEGORY"),
    queryFn: () => fetchFeeProfiles("CATEGORY"),
    staleTime: 30_000,
  });
  const marketProfilesQ = useQuery({
    queryKey: feeKeys.profiles("MARKET"),
    queryFn: () => fetchFeeProfiles("MARKET"),
    staleTime: 30_000,
  });
  const categoriesQ = useQuery({
    queryKey: keys.categoriesList(PAGE_FETCH_LIMIT, 0),
    queryFn: () => fetchCategories({ limit: PAGE_FETCH_LIMIT, offset: 0 }),
  });
  const marketsQ = useQuery({
    queryKey: keys.rulesList(
      "OPEN",
      PAGE_FETCH_LIMIT,
      0,
      { by: "created_at", dir: "desc" },
      {},
    ),
    queryFn: () =>
      fetchRules({
        limit: PAGE_FETCH_LIMIT,
        offset: 0,
        status: "OPEN",
        sort: { by: "created_at", dir: "desc" },
      }),
  });

  // Which books this policy's markets run, and so which currencies its
  // rate is collected in (SM-503).
  const reachIndex = useFeeReach();

  const categories = categoriesQ.data?.items ?? [];
  const markets = marketsQ.data?.items ?? [];
  // Counts computed over the fetched window are only quotable when the
  // window covers the whole catalog (same guard as the Policies list).
  const windowComplete =
    markets.length >= (marketsQ.data?.page.total ?? 0);
  const globalProfile = globalQ.data?.[0] ?? null;
  const categoryProfiles = categoryProfilesQ.data ?? [];
  const marketProfiles = marketProfilesQ.data ?? [];

  // For MARKET scopes fetch the market itself: the page-level window
  // only covers the first page of OPEN markets, and the fallback copy
  // ("what happens if I remove this?") needs the real category.
  const marketQ = useQuery({
    queryKey: keys.ruleDetail(profile.scope_id),
    queryFn: () => fetchRule(profile.scope_id),
    enabled: profile.scope === "MARKET",
    staleTime: 5 * 60_000,
  });

  // A MARKET policy takes the row this page fetched for itself, which
  // covers markets outside the catalog window the hook loaded; every
  // other scope is answered from that window.
  const reach =
    profile.scope === "MARKET" && marketQ.data
      ? reachIndex.forMarketRow(marketQ.data)
      : reachIndex.forScope(profile.scope, profile.scope_id);

  // A legacy policy has no currency of its own — it still prices every
  // book at its level — so its example falls back to Gold. Any other
  // policy is quoted in the one currency it prices (AC 9).
  // A rate whose reach could not be read is still quoted somewhere, and
  // Gold is the only honest guess: every market carries the Cash book.
  const exampleCurrencies = [exampleCurrency(profile.currency)];
  const legacy = isLegacyPolicy(profile);

  const headline = isGlobal
    ? "Platform default"
    : profile.scope === "CATEGORY"
    ? categories.find((c) => c.category_id === profile.scope_id)?.name ??
      profile.scope_id
    : marketQ.data?.name ?? profile.scope_id;

  // Category context for a market override; markets affected for a
  // category override.
  const marketItem = profile.scope === "MARKET" ? marketQ.data : undefined;
  const marketCategory = marketItem
    ? categories.find((c) => c.category_id === marketItem.category_id)
    : undefined;

  const affectedMarkets = useMemo(() => {
    if (profile.scope === "MARKET") {
      return marketItem ? [marketItem] : [];
    }
    if (profile.scope === "CATEGORY") {
      return markets.filter((m) => m.category_id === profile.scope_id);
    }
    return [];
  }, [profile, markets, marketItem]);

  const overriddenMarketIds = useMemo(
    () => new Set(marketProfiles.map((p) => p.scope_id)),
    [marketProfiles],
  );
  const categoriesWithOverride = useMemo(
    () => new Set(categoryProfiles.map((p) => p.scope_id)),
    [categoryProfiles],
  );
  const affectedCount = useMemo(() => {
    if (profile.scope === "MARKET") return 1;
    if (profile.scope === "CATEGORY") {
      return affectedMarkets.filter(
        (m) => !overriddenMarketIds.has(m.market_id),
      ).length;
    }
    let n = 0;
    for (const m of markets) {
      if (overriddenMarketIds.has(m.market_id)) continue;
      if (categoriesWithOverride.has(m.category_id)) continue;
      n++;
    }
    return n;
  }, [profile.scope, affectedMarkets, markets, overriddenMarketIds, categoriesWithOverride]);

  // ── the redemption fee this policy inherits (SM-332) ─────────────
  //
  // What its markets would pay if it set nothing. The platform default
  // has nothing above it, so it always sets a rate; everything else can
  // choose. Market overrides look to their category first — the same
  // chain the server walks.
  const inheritedRedemption = useMemo(() => {
    if (profile.scope === "MARKET" && marketItem) {
      const cat = categoryProfiles.find(
        (p) => p.scope_id === marketItem.category_id,
      );
      if (setsRedemptionRate(cat)) {
        return {
          rate: redemptionRateOf(cat),
          from: marketCategory
            ? `the ${marketCategory.name} category override`
            : "its category override",
        };
      }
    }
    return {
      rate: redemptionRateOf(globalProfile),
      from: setsRedemptionRate(globalProfile)
        ? "the platform default"
        : "the platform default, which sets none",
    };
  }, [profile.scope, marketItem, marketCategory, categoryProfiles, globalProfile]);

  // ── form state ──────────────────────────────────────────────────
  const [maker, setMaker] = useState(profile.maker_fee_rate);
  const [taker, setTaker] = useState(profile.taker_fee_rate);
  const [redeemMode, setRedeemMode] = useState<RedemptionMode>(
    isGlobal || setsRedemptionRate(profile) ? "set" : "inherit",
  );
  const [redeem, setRedeem] = useState(redemptionRateOf(profile));
  const [note, setNote] = useState("");
  const [effectiveAt, setEffectiveAt] = useState(
    isScheduled ? profile.effective_at : "",
  );
  useEffect(() => {
    setMaker(profile.maker_fee_rate);
    setTaker(profile.taker_fee_rate);
    setRedeemMode(isGlobal || setsRedemptionRate(profile) ? "set" : "inherit");
    setRedeem(redemptionRateOf(profile));
    setNote("");
    setEffectiveAt(isScheduled ? profile.effective_at : "");
  }, [
    profile.profile_id,
    profile.maker_fee_rate,
    profile.taker_fee_rate,
    profile.redemption_fee_rate,
    profile.effective_at,
    isGlobal,
    isScheduled,
  ]);

  // Moving the redemption fee needs the treasury permission on top of
  // whatever reaches this page. Optimistically allowed while perms
  // load, like every other mutation gate here: the server rejects
  // regardless, and gating on `has` alone flashes disabled controls on
  // every cold load.
  const perms = useMyPerms();
  const canEditRedemption =
    !perms.loaded || perms.isSuperAdmin || perms.has(Perm.FeeConfigUpdate);

  const redeemWasSet = setsRedemptionRate(profile);
  const redeemNowSet = redeemMode === "set";
  const redeemDirty =
    redeemWasSet !== redeemNowSet ||
    (redeemNowSet && redeem !== redemptionRateOf(profile));

  const dirty =
    maker !== profile.maker_fee_rate ||
    taker !== profile.taker_fee_rate ||
    redeemDirty ||
    (isScheduled && effectiveAt !== profile.effective_at);

  const saveMut = useMutation({
    mutationFn: () =>
      createFeeProfile({
        scope: profile.scope,
        scope_id: profile.scope_id,
        // An edit writes a successor at the SAME (scope, currency): the
        // currency is this policy's identity, not one of its fields, and
        // moving it here would silently retire one currency's policy and
        // create another's (SM-510).
        //
        // A legacy policy is the one row with no currency of its own, so
        // editing it would have to CHOOSE one — a create, not an edit.
        // The rate inputs below are read-only on such a row and point at
        // the New policy page instead, so this branch is unreachable
        // for one.
        currency: exampleCurrencies[0],
        maker_fee_rate: maker || "0",
        taker_fee_rate: taker || "0",
        fee_rate_precision: precision,
        // Say nothing about the redemption fee unless it actually
        // moved. The server carries the predecessor's value forward on
        // silence, which is what keeps a taker-only edit from dropping
        // a redemption override.
        ...(redeemDirty
          ? redeemNowSet
            ? { redemption_fee_rate: redeem || "0" }
            : { clear_redemption_fee_rate: true }
          : {}),
        note: note.trim() || undefined,
        effective_at: effectiveAt || undefined,
      }),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: feeKeys.all });
      qc.invalidateQueries({ queryKey: ["admin-rules"] });
      qc.invalidateQueries({ queryKey: revenueKeys.all });
      if (created?.profile_id && created.profile_id !== profile.profile_id) {
        navigate({
          to: "/treasury/fees/policies/$profileId",
          params: { profileId: created.profile_id },
          replace: true,
        });
      }
    },
    meta: {
      successMessage: effectiveAt ? "Fee change scheduled." : "Fee change saved.",
    },
  });

  const archiveMut = useMutation({
    mutationFn: (reason: string) =>
      isScheduled
        ? cancelScheduledFeeProfile(profile.profile_id, reason)
        : archiveFeeProfile(profile.profile_id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: feeKeys.all });
      qc.invalidateQueries({ queryKey: ["admin-rules"] });
      navigate({ to: "/treasury/fees/policies" });
    },
    meta: {
      successMessage: isScheduled
        ? "Scheduled fee change canceled."
        : "Override removed.",
    },
  });
  const [confirmingArchive, setConfirmingArchive] = useState(false);

  // History (compact) for this scope.
  const auditQ = useQuery({
    queryKey: feeKeys.audit(profile.scope, profile.scope_id, 5, 0),
    queryFn: () =>
      fetchFeeAudit({
        limit: 5,
        offset: 0,
        scope: profile.scope,
        scope_id: profile.scope_id,
      }),
    staleTime: 30_000,
  });

  const fallbackLabel =
    profile.scope === "MARKET"
      ? marketCategory &&
        categoryProfiles.some((p) => p.scope_id === marketCategory.category_id)
        ? `the ${marketCategory.name} category override`
        : marketQ.data
        ? "the platform default"
        : "its category override, or the platform default"
      : "the platform default";

  return (
    <div className="space-y-5">
      <div>
        <Link
          to="/treasury/fees/policies"
          className="text-2xs font-medium text-muted hover:text-fg"
        >
          ← Fee policies
        </Link>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="truncate font-display text-2xl tracking-tight">
              {headline}
            </h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-2xs">
              <Badge tone={scopeTone(profile.scope)}>
                {scopeNoun(profile.scope)}
              </Badge>
              {isScheduled && <Badge tone="warning">Scheduled</Badge>}
              {isArchived && <Badge tone="muted">Archived</Badge>}
              <span className="text-t-3">
                Version {profile.version.replace(/^v/, "")}
              </span>
              {marketCategory && (
                <span className="text-t-4">· in {marketCategory.name}</span>
              )}
              <span className="flex items-center gap-1.5 text-t-4">
                <span aria-hidden>·</span>
                <PolicyCurrencyCell currency={profile.currency} />
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isGlobal && (
              <CopyIdButton
                id={profile.scope_id}
                label={
                  profile.scope === "MARKET"
                    ? "Copy market ID"
                    : "Copy category ID"
                }
              />
            )}
            {!isGlobal && !isArchived && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirmingArchive(true)}
                disabled={archiveMut.isPending}
              >
                {isScheduled ? "Cancel scheduled change" : "Remove override"}
              </Button>
            )}
          </div>
        </div>
      </div>

      {isArchived && (
        <p className="rounded-md border border-line-1 bg-surface-2 px-3 py-2.5 text-2xs leading-relaxed text-t-2">
          This version is no longer active. Saving below creates a fresh
          version with the rates you set.
        </p>
      )}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,360px)]">
        <Card className="self-start">
          <CardHeader>
            <CardTitle>Rates</CardTitle>
          </CardHeader>
          <CardBody className="space-y-5">
            {legacy && (
              <p className="rounded-md border border-line-1 bg-surface-2 px-3 py-2.5 text-2xs leading-relaxed text-t-2">
                {LEGACY_POLICY_NOTE}
              </p>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <FeeRateInput
                label="Taker fee"
                value={taker}
                precision={precision}
                onChange={setTaker}
                disabled={legacy}
                currencies={exampleCurrencies}
              />
              <FeeRateInput
                label="Maker fee"
                value={maker}
                precision={precision}
                onChange={setMaker}
                disabled={legacy}
                showPreview={false}
                hint="Charged to the resting side of a fill. Usually zero."
              />
            </div>

            <RedemptionFeeField
              mode={redeemMode}
              onModeChange={setRedeemMode}
              value={redeem}
              onChange={setRedeem}
              precision={precision}
              currency={exampleCurrencies[0]}
              inheritedRate={inheritedRedemption.rate}
              inheritedFrom={inheritedRedemption.from}
              canInherit={!isGlobal}
              disabled={legacy || !canEditRedemption}
              disabledHint={
                legacy
                  ? "This rate was set before fees were split by currency. Save a policy for one currency to replace it there."
                  : canEditRedemption
                  ? undefined
                  : "Your role can view the redemption fee but not change it."
              }
            />

            {redemptionChargeNote(exampleCurrencies[0]) && (
              <p className="rounded-md border border-line-1 bg-surface-2 px-3 py-2.5 text-2xs leading-relaxed text-t-2">
                {redemptionChargeNote(exampleCurrencies[0])}
              </p>
            )}

            <ScheduleField
              key={profile.profile_id}
              value={effectiveAt}
              onChange={setEffectiveAt}
            />

            <div>
              <label
                htmlFor="policy-note"
                className="text-2xs font-semibold uppercase tracking-wide text-t-3"
              >
                Note (audit log)
              </label>
              <input
                id="policy-note"
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Why this change?"
                className="mt-1.5 h-10 w-full rounded-md border border-line-1 bg-bg-2 px-3 text-sm text-t-1 placeholder:text-t-4 focus:border-line-3 focus:outline-none focus:ring-4 focus:ring-neon/15"
              />
            </div>

            {saveMut.isError && (
              <p className="text-2xs text-danger">
                {(saveMut.error as Error).message}
              </p>
            )}
            {saveMut.isError && <BrokerCeilingHint error={saveMut.error} />}

            <div className="flex flex-wrap items-center gap-3">
              {legacy ? (
                // Editing a legacy row in place would have to invent a
                // currency for it, which is a create with a different
                // meaning: replacing the shared rate for ONE currency
                // and leaving the rest of them on it. Sent to the page
                // that says so.
                <Link
                  to="/treasury/fees/policies/new"
                  search={{
                    scope:
                      profile.scope === "GLOBAL"
                        ? "platform"
                        : profile.scope === "CATEGORY"
                        ? "category"
                        : "market",
                  }}
                  className="inline-flex h-9 items-center rounded-md bg-neon px-3 text-2xs font-semibold text-black hover:brightness-110"
                >
                  Set a rate for one currency
                </Link>
              ) : (
                <Button
                  onClick={() => saveMut.mutate()}
                  disabled={!dirty || saveMut.isPending}
                  loading={saveMut.isPending}
                >
                  {effectiveAt ? "Schedule change" : "Save changes"}
                </Button>
              )}
              <span className="text-2xs text-t-3">
                {effectiveAt
                  ? `Activates ${fmtLocal(effectiveAt)}.`
                  : isGlobal && !windowComplete
                  ? "Applies immediately to every market without a more specific override."
                  : `Applies immediately to ${affectedCount} ${
                      affectedCount === 1 ? "market" : "markets"
                    }.`}
              </span>
            </div>

            {/* A legacy policy still prices every book at its level, so
                saving one moves all of them. Said where the rate is
                saved, because it is the one case where a single rate is
                not a single currency (SM-510). */}
            {legacy && (
              <p className="text-2xs leading-relaxed text-t-3">
                {LEGACY_POLICY_NOTE}
              </p>
            )}
          </CardBody>
        </Card>

        <div className="space-y-5">
          <FeeReachPanel
            reach={reach}
            currency={legacy ? undefined : exampleCurrencies[0]}
            loading={reachIndex.loading}
            emptyDescription={
              profile.scope === "MARKET"
                ? "This market has no book open, so it charges nothing right now. The rate applies the moment one opens."
                : undefined
            }
          />

          <Card>
            <CardBody className="space-y-1.5 text-2xs leading-relaxed text-t-2">
              <div className="font-semibold uppercase tracking-wide text-t-3">
                How this resolves
              </div>
              {isGlobal ? (
                <p>
                  The last stop in the chain. Every market without a category
                  or market override charges this rate.
                </p>
              ) : profile.scope === "CATEGORY" ? (
                <p>
                  Beats the platform default
                  {globalProfile
                    ? ` (${fmtPct(
                        globalProfile.taker_fee_rate,
                        globalProfile.fee_rate_precision,
                      )} taker)`
                    : ""}{" "}
                  for markets in this category without their own override.
                  Removing it sends those markets back to the platform
                  default.
                </p>
              ) : (
                <p>
                  The most specific rate for this market. Removing it sends the
                  market back to {fallbackLabel}.
                </p>
              )}
              {/* The redemption fee walks the same three levels but
                  independently, so a policy can win the trading rates
                  and still inherit this one. Stating it separately is
                  what stops an operator reading the line above as
                  covering all three. */}
              <p className="pt-1 text-t-3">
                {isGlobal
                  ? setsRedemptionRate(profile)
                    ? `Redeem is set here at ${percentLabel(redemptionRateOf(profile), precision)} and applies wherever no category or market sets its own.`
                    : "Redeem is not set anywhere, so redemptions credit the full payout."
                  : redeemWasSet
                    ? `Redeem is set here at ${percentLabel(redemptionRateOf(profile), precision)}. Switching it to Inherit sends these markets back to ${percentLabel(inheritedRedemption.rate, precision)} from ${inheritedRedemption.from}.`
                    : `Redeem is inherited: ${percentLabel(inheritedRedemption.rate, precision)} from ${inheritedRedemption.from}. Set it here to override just that fee.`}
              </p>
            </CardBody>
          </Card>

          {!isGlobal && affectedMarkets.length > 0 && (
            <Card>
              <CardBody className="space-y-1.5">
                <div className="text-2xs font-semibold uppercase tracking-wide text-t-3">
                  Affected markets · {affectedMarkets.length}
                </div>
                <ul className="space-y-1 text-2xs">
                  {affectedMarkets.slice(0, 6).map((m) => (
                    <li key={m.market_id} className="truncate text-t-2">
                      {m.name}
                      {profile.scope === "CATEGORY" &&
                        overriddenMarketIds.has(m.market_id) && (
                          <span className="ml-1.5 text-t-4">
                            (has its own override)
                          </span>
                        )}
                    </li>
                  ))}
                  {affectedMarkets.length > 6 && (
                    <li className="text-t-4">
                      + {affectedMarkets.length - 6} more
                    </li>
                  )}
                </ul>
              </CardBody>
            </Card>
          )}

          <div>
            <div className="mb-1.5 flex items-baseline justify-between">
              <span className="text-2xs font-semibold uppercase tracking-wide text-t-3">
                History
              </span>
              <Link
                to="/treasury/fees/activity"
                search={{ scope: profile.scope, id: profile.scope_id }}
                className="text-2xs text-muted hover:text-fg"
              >
                View all in Activity →
              </Link>
            </div>
            {auditQ.isLoading ? (
              <Skeleton className="h-16 w-full" />
            ) : auditQ.isError ? (
              auditQ.error instanceof APIError && auditQ.error.status === 403 ? (
                <p className="text-2xs text-t-4">
                  Your role doesn&apos;t include audit-log access, so history
                  is hidden here.
                </p>
              ) : (
                <LoadFailed
                  what="history"
                  error={auditQ.error}
                  onRetry={() => void auditQ.refetch()}
                />
              )
            ) : (auditQ.data?.items.length ?? 0) === 0 ? (
              <p className="text-2xs text-t-4">No changes recorded yet.</p>
            ) : (
              <AuditList items={auditQ.data!.items} showTarget={false} />
            )}
          </div>
        </div>
      </div>

      <ConfirmModal
        open={confirmingArchive}
        tone="danger"
        title={
          isScheduled
            ? "Cancel this scheduled change?"
            : `Remove this ${scopeNoun(profile.scope).toLowerCase()}?`
        }
        description={
          isScheduled ? (
            <p>
              It won&apos;t activate. The rate that is live right now stays in
              effect.
            </p>
          ) : (
            <div className="space-y-2">
              <p>
                {profile.scope === "MARKET"
                  ? `This market goes back to ${fallbackLabel}. `
                  : "Markets in this category go back to the platform default. "}
                The change is recorded in the audit log.
              </p>
              {legacy && (
                <p>
                  This policy was set before fees were split by currency, so
                  archiving it removes the rate for every currency at once.
                </p>
              )}
              {redeemWasSet && (
                <p>
                  That includes the redemption fee: it goes from{" "}
                  <strong>
                    {percentLabel(redemptionRateOf(profile), precision)}
                  </strong>{" "}
                  to <strong>{percentLabel(inheritedRedemption.rate, precision)}</strong>{" "}
                  from {inheritedRedemption.from}. Past redemptions keep the fee
                  they were charged.
                </p>
              )}
            </div>
          )
        }
        confirmLabel={isScheduled ? "Cancel change" : "Remove"}
        cancelLabel="Keep it"
        reasonLabel="Reason (audit log)"
        reasonRequired
        loading={archiveMut.isPending}
        error={archiveMut.isError ? (archiveMut.error as Error).message : null}
        onConfirm={(reason) => archiveMut.mutate(reason)}
        onCancel={() => {
          archiveMut.reset();
          setConfirmingArchive(false);
        }}
      />
    </div>
  );
}

function CopyIdButton({ id, label }: { id: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      title={id}
      onClick={() => {
        void navigator.clipboard?.writeText(id);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="rounded-md border border-line-1 bg-bg-2 px-2.5 py-1 text-2xs font-medium text-t-3 hover:border-line-3 hover:text-fg"
    >
      {copied ? "Copied" : label}
    </button>
  );
}

function fmtLocal(rfc3339: string): string {
  const d = new Date(rfc3339);
  if (Number.isNaN(d.getTime())) return rfc3339;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
